import type {FC} from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Link,
  List,
  ListItem,
  Text,
} from "@chakra-ui/react";
import {BsCheckLg} from "react-icons/bs";
import type {ICreditPackage} from "../../models/interfaces/credits.interface.ts";

export const CREDITS_CONTACT_EMAIL = "ayal@glix.ai";

// C7 action matrix: "none" renders no control (members, or an action the balance does not allow);
// "scheduled" is the plan already stored as nextPlanId for the next period.
export type PricingCtaState =
  | "get_started"
  | "contact_us"
  | "current"
  | "added"
  | "upgrade"
  | "downgrade"
  | "scheduled"
  | "none";

export type PricingCardAction = "purchase" | "upgrade" | "downgrade";

const formatUsd = (amount: number): string => `$${amount.toLocaleString("en-US")}`;

// The app runs in Chakra dark mode; the white panel needs explicit light colors on every control.
const solidButtonStyle = {
  bg: "brandPurple.500",
  color: "white",
  _hover: {bg: "violetPurple.500"},
  _active: {bg: "brandPurple.600"},
} as const;

const outlineButtonStyle = {
  variant: "outline",
  bg: "white",
  borderColor: "n.300",
  color: "n.700",
  _hover: {bg: "n.200"},
  _active: {bg: "n.300"},
} as const;

const currentPlanStyle = {
  bg: "brandPurple.100",
  color: "brandPurple.700",
  borderWidth: "1px",
  borderColor: "brandPurple.300",
  _disabled: {opacity: 1, cursor: "not-allowed"},
  _hover: {_disabled: {bg: "brandPurple.100"}},
} as const;

const scheduledPlanStyle = {
  bg: "n.200",
  color: "n.700",
  borderWidth: "1px",
  borderColor: "n.300",
  _disabled: {opacity: 1, cursor: "not-allowed"},
  _hover: {_disabled: {bg: "n.200"}},
} as const;

const creditsAddedStyle = {
  bg: "success.100",
  color: "success.900",
  borderWidth: "1px",
  borderColor: "success.500",
  _disabled: {opacity: 1, cursor: "default"},
  _hover: {_disabled: {bg: "success.100"}},
} as const;

const ctaButtonStyle = {
  w: "full",
  size: "md",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
} as const;

interface PackageCtaProps {
  pkg: ICreditPackage;
  ctaState: PricingCtaState;
  isPending: boolean;
  isLocked: boolean;
  onAction: (pkg: ICreditPackage, action: PricingCardAction) => void;
}

const PackageCta: FC<PackageCtaProps> = ({pkg, ctaState, isPending, isLocked, onAction}) => {
  switch (ctaState) {
    case "contact_us":
      return (
        <Button
          as="a"
          href={`mailto:${pkg.contactEmail ?? CREDITS_CONTACT_EMAIL}`}
          {...ctaButtonStyle}
          {...outlineButtonStyle}
        >
          Contact us
        </Button>
      );
    case "current":
      return (
        <Button isDisabled {...ctaButtonStyle} {...currentPlanStyle}>
          Current plan
        </Button>
      );
    case "scheduled":
      return (
        <Button isDisabled {...ctaButtonStyle} {...scheduledPlanStyle}>
          Scheduled next period
        </Button>
      );
    case "added":
      return (
        <Button isDisabled leftIcon={<BsCheckLg/>} {...ctaButtonStyle} {...creditsAddedStyle}>
          Credits added
        </Button>
      );
    case "upgrade":
      return (
        <Button
          onClick={() => onAction(pkg, "upgrade")}
          isDisabled={isLocked}
          {...ctaButtonStyle}
          {...solidButtonStyle}
        >
          Upgrade
        </Button>
      );
    case "downgrade":
      return (
        <Button
          onClick={() => onAction(pkg, "downgrade")}
          isDisabled={isLocked}
          {...ctaButtonStyle}
          {...outlineButtonStyle}
        >
          Downgrade at renewal
        </Button>
      );
    case "none":
      return null;
    default:
      return (
        <Button
          onClick={() => onAction(pkg, "purchase")}
          isLoading={isPending}
          isDisabled={isLocked}
          {...ctaButtonStyle}
          {...(pkg.highlight ? solidButtonStyle : outlineButtonStyle)}
        >
          Get started
        </Button>
      );
  }
};

interface PricingPackageCardProps extends PackageCtaProps {
  bullets: string[];
}

const PricingPackageCard: FC<PricingPackageCardProps> = (
  {pkg, bullets, ctaState, isPending, isLocked, onAction},
) => {
  const isCurrent = ctaState === "current";
  const isScheduled = ctaState === "scheduled";
  const creditsLabel = pkg.unlimited || pkg.credits === null
    ? "Unlimited Credits"
    : `${pkg.credits.toLocaleString("en-US")} Credits`;

  return (
    <Flex
      direction="column"
      position="relative"
      overflow="hidden"
      bg="white"
      borderWidth="1px"
      borderColor={pkg.highlight || isCurrent ? "brandPurple.400" : "n.300"}
      rounded="2xl"
      p={5}
      minH={{md: "24rem"}}
      data-testid={`credit-package-${pkg.id}`}
    >
      {pkg.highlight && (
        <Box
          position="absolute"
          top={5}
          right={-10}
          w="10rem"
          py={1}
          transform="rotate(45deg)"
          bg="brandPurple.500"
          color="white"
          fontSize="xs"
          lineHeight="1rem"
          fontWeight="semibold"
          textAlign="center"
        >
          Most Popular
        </Box>
      )}
      <Flex align="center" gap={2}>
        <Text as="h3" fontSize="md" fontWeight="semibold" color="n.700">{pkg.name}</Text>
        {isCurrent && (
          <Badge
            bg="brandPurple.100"
            color="brandPurple.700"
            rounded="full"
            px={2}
            fontSize="xs"
            fontWeight="semibold"
            textTransform="none"
          >
            Current
          </Badge>
        )}
        {isScheduled && (
          <Badge
            bg="n.200"
            color="n.700"
            rounded="full"
            px={2}
            fontSize="xs"
            fontWeight="semibold"
            textTransform="none"
          >
            Next period
          </Badge>
        )}
      </Flex>
      <Text fontSize="xl" fontWeight="bold" color="n.700" mt={1}>
        {pkg.priceUsd === null ? (
          <>
            Price -{" "}
            <Link
              href={`mailto:${pkg.contactEmail ?? CREDITS_CONTACT_EMAIL}`}
              color="brandPurple.500"
              textDecoration="underline"
              _hover={{color: "brandPurple.600"}}
            >
              Contact us
            </Link>
          </>
        ) : pkg.interval === "month" ? (
          `${formatUsd(pkg.priceUsd)} / month`
        ) : (
          formatUsd(pkg.priceUsd)
        )}
      </Text>
      <Box
        mt={4}
        px={3}
        py={2}
        borderWidth="1px"
        borderColor="n.300"
        rounded="lg"
        fontSize="lg"
        fontWeight="bold"
        color="n.700"
      >
        {creditsLabel}
      </Box>
      <List spacing={2} mt={4}>
        {bullets.map((bullet) => (
          <ListItem
            key={bullet}
            display="flex"
            alignItems="center"
            gap={2}
            fontSize="xs"
            fontWeight="semibold"
            color="n.700"
          >
            <Box as="span" flexShrink={0} w={1.5} h={1.5} rounded="full" bg="brandPurple.500"/>
            {bullet}
          </ListItem>
        ))}
      </List>
      <Box mt="auto" pt={8}>
        <PackageCta
          pkg={pkg}
          ctaState={ctaState}
          isPending={isPending}
          isLocked={isLocked}
          onAction={onAction}
        />
      </Box>
    </Flex>
  );
};

export default PricingPackageCard;
